"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Wallet,
  IndianRupee,
  ArrowUpRight,
  AlertTriangle,
  Clock,
  Shield,
  CheckCircle,
  Banknote,
  TrendingDown,
} from "lucide-react";

interface WalletWithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWithdraw: (amount: number) => void;
  availableBalance: number;
}

const quickAmounts = [500, 1000, 5000, 10000];
const MIN_WALLET_AMOUNT = 1;

export function WalletWithdrawModal({
  isOpen,
  onClose,
  onWithdraw,
  availableBalance,
}: WalletWithdrawModalProps) {
  const [amount, setAmount] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Convert balance from paise to rupees for display and comparison
  const availableBalanceInRupees = availableBalance / 100;
  const numericAmount = parseInt(amount) || 0;
  const remainingBalance = availableBalanceInRupees - numericAmount;
  const maxWithdrawable = availableBalanceInRupees;

  const handleQuickAmount = (quickAmount: number) => {
    if (quickAmount <= availableBalanceInRupees) {
      setAmount(quickAmount.toString());
    }
  };

  const handleAmountChange = (value: string) => {
    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, "");
    setAmount(numericValue);
  };

  const handleMaxWithdraw = () => {
    setAmount(Math.floor(maxWithdrawable).toString());
  };

  const handleWithdraw = async () => {
    if (!numericAmount || numericAmount <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    if (numericAmount < MIN_WALLET_AMOUNT) {
      alert(`Minimum withdrawal amount is ₹${MIN_WALLET_AMOUNT}`);
      return;
    }

    if (numericAmount > availableBalanceInRupees) {
      alert("Insufficient balance");
      return;
    }

    if (numericAmount > 50000) {
      alert("Maximum withdrawal amount is ₹50,000 per transaction");
      return;
    }

    setIsProcessing(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));
      onWithdraw(numericAmount);
      setAmount("");
      onClose();
    } catch (error) {
      console.error("Error withdrawing money:", error);
      alert("Failed to process withdrawal. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetAndClose = () => {
    setAmount("");
    setIsProcessing(false);
    onClose();
  };

  const getAmountStatus = () => {
    if (!numericAmount) return null;
    if (numericAmount < MIN_WALLET_AMOUNT)
      return {
        type: "error",
        message: `Minimum withdrawal is ₹${MIN_WALLET_AMOUNT}`,
      };
    if (numericAmount > availableBalanceInRupees)
      return { type: "error", message: "Insufficient balance" };
    if (numericAmount > 50000)
      return { type: "error", message: "Maximum withdrawal is ₹50,000" };
    return { type: "success", message: "Valid withdrawal amount" };
  };

  const status = getAmountStatus();

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-lg bg-gradient-to-br from-card/95 to-card/90 backdrop-blur-sm border border-border/40 shadow-2xl">
        <DialogHeader className="pb-6 border-b border-border/40">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-red-100 to-red-50 rounded-xl">
              <ArrowUpRight className="h-6 w-6 text-red-600" />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
                Withdraw from Wallet
                <TrendingDown className="h-4 w-4 text-red-600" />
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Transfer funds from your wallet
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Available Balance */}
          <div className="bg-gradient-to-r from-primary/10 to-transparent rounded-xl border border-primary/20 p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Available Balance
                </p>
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  <p className="text-2xl font-bold text-primary">
                    ₹
                    {availableBalanceInRupees.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleMaxWithdraw}
                disabled={maxWithdrawable <= 0}
                className="border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50 disabled:opacity-50"
              >
                Withdraw Max
              </Button>
            </div>
          </div>

          {/* Quick Amount Selection */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <h3 className="text-sm font-semibold text-foreground">
                Quick Select
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {quickAmounts.map((quickAmount) => {
                const isDisabled = quickAmount > availableBalanceInRupees;
                return (
                  <Button
                    key={quickAmount}
                    variant={
                      amount === quickAmount.toString() ? "default" : "outline"
                    }
                    onClick={() => handleQuickAmount(quickAmount)}
                    disabled={isDisabled}
                    className={`h-12 text-sm font-medium rounded-xl transition-all duration-200 ${
                      amount === quickAmount.toString()
                        ? "bg-red-600 text-white shadow-lg scale-105 border-red-600"
                        : isDisabled
                          ? "border-border/30 text-muted-foreground/50 cursor-not-allowed"
                          : "border-border/60 hover:bg-red-50 hover:border-red-200 hover:scale-105"
                    }`}
                  >
                    <IndianRupee className="h-3 w-3 mr-1" />
                    {quickAmount >= 1000
                      ? `${quickAmount / 1000}K`
                      : quickAmount}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Custom Amount Input */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <h3 className="text-sm font-semibold text-foreground">
                Enter Withdrawal Amount
              </h3>
            </div>

            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center">
                <IndianRupee className="h-6 w-6 text-red-600" />
              </div>
              <Input
                type="text"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="Enter amount"
                className={`pl-14 h-16 text-2xl font-bold text-center bg-card/50 border-border/60 focus:ring-red-200 focus:border-red-400 rounded-xl shadow-inner ${
                  status?.type === "error" ? "border-red-300 bg-red-50/50" : ""
                }`}
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                {numericAmount > 0 && (
                  <Badge
                    className={`font-semibold ${
                      status?.type === "error"
                        ? "bg-red-100 text-red-800 border-red-200"
                        : "bg-green-100 text-green-800 border-green-200"
                    }`}
                  >
                    ₹
                    {numericAmount.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Badge>
                )}
              </div>
            </div>

            {/* Status Message */}
            {status && (
              <div
                className={`flex items-center gap-2 text-sm ${
                  status.type === "error" ? "text-red-600" : "text-green-600"
                }`}
              >
                {status.type === "error" ? (
                  <AlertTriangle className="h-4 w-4" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                {status.message}
              </div>
            )}
          </div>

          {/* Remaining Balance Preview */}
          {numericAmount > 0 && remainingBalance >= 0 && (
            <div className="bg-gradient-to-r from-green-50 to-transparent rounded-xl border border-green-200 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Withdrawal Amount</span>
                <span className="font-medium text-red-600">
                  -₹
                  {numericAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="h-px bg-border/40 my-2"></div>
              <div className="flex items-center justify-between font-semibold">
                <span className="text-foreground">Remaining Balance</span>
                <span className="text-green-600 text-lg">
                  ₹
                  {remainingBalance.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}

          {/* Processing Time Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-800">
                Processing Time
              </p>
              <p className="text-xs text-blue-700">
                Withdrawals are typically processed within 2-4 business hours
              </p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-3">
            <Shield className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-green-800">
                Secure Withdrawal
              </p>
              <p className="text-xs text-green-700">
                Your withdrawal request is processed securely and verified
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-6 border-t border-border/40">
          <Button
            variant="outline"
            onClick={resetAndClose}
            disabled={isProcessing}
            className="text-sm h-10 px-6 border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 hover:border-gray-300 rounded-lg"
          >
            Cancel
          </Button>
          <Button
            onClick={handleWithdraw}
            disabled={
              !numericAmount ||
              numericAmount < MIN_WALLET_AMOUNT ||
              numericAmount > availableBalanceInRupees ||
              isProcessing
            }
            className="bg-gradient-to-r from-red-600 to-red-500 text-white hover:from-red-700 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm h-10 px-6 shadow-lg rounded-lg flex items-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                Processing...
              </>
            ) : (
              <>
                <Banknote className="h-4 w-4" />
                Withdraw ₹
                {numericAmount.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
